import React, { useMemo, useState } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, ExpressCheckoutElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Loader2, Lock, ShieldCheck } from 'lucide-react';

const STRIPE_PK = process.env.REACT_APP_STRIPE_PK;
const stripePromise = STRIPE_PK ? loadStripe(STRIPE_PK) : null;

const APPEARANCE = {
  theme: 'night',
  labels: 'floating',
  variables: {
    colorPrimary: '#F82C47',
    colorBackground: '#1a1a1c',
    colorText: '#ffffff',
    colorTextSecondary: '#9ca3af',
    colorDanger: '#F82C47',
    fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif',
    fontSizeBase: '15px',
    borderRadius: '16px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': {
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.16)',
      boxShadow: 'none',
      padding: '14px 14px',
    },
    '.Input:focus': {
      border: '1px solid #F82C47',
      boxShadow: '0 0 0 3px rgba(248,44,71,0.18)',
    },
    '.Label': {
      color: 'rgba(255,255,255,0.65)',
      fontWeight: '500',
    },
    '.Tab': {
      backgroundColor: 'rgba(255,255,255,0.05)',
      border: '1px solid rgba(255,255,255,0.16)',
      boxShadow: 'none',
    },
    '.Tab--selected': {
      backgroundColor: 'rgba(248,44,71,0.18)',
      border: '1px solid #F82C47',
    },
    '.TabLabel': { color: '#ffffff' },
  },
};

const InnerForm = ({ amount, eventName, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [walletReady, setWalletReady] = useState(false);

  const returnUrl = `${window.location.origin}/payment-success?stripe=intent`;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError('');

    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
    });

    if (stripeError) {
      const msg = stripeError.message || 'Pago no completado';
      setError(msg);
      setSubmitting(false);
      if (onError) onError(msg);
    }
  };

  // Apple Pay / Google Pay confirmation via Express Checkout
  const handleExpressConfirm = async (event) => {
    if (!stripe) { event.paymentFailed({ reason: 'fail' }); return; }
    const { error: stripeError } = await stripe.confirmPayment({
      elements,
      clientSecret: undefined, // Elements provides it via options
      confirmParams: { return_url: returnUrl },
    });
    if (stripeError) {
      event.paymentFailed({ reason: 'fail' });
      setError(stripeError.message || 'Pago no completado');
      if (onError) onError(stripeError.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4" data-testid="embedded-payment-form">
      {/* Apple Pay / Google Pay buttons — Stripe only renders content if device supports them */}
      <ExpressCheckoutElement
        onReady={({ availablePaymentMethods }) => {
          if (availablePaymentMethods && (availablePaymentMethods.applePay || availablePaymentMethods.googlePay)) {
            setWalletReady(true);
          }
        }}
        onConfirm={handleExpressConfirm}
        options={{
          paymentMethods: { applePay: 'auto', googlePay: 'auto', link: 'never' },
          buttonHeight: 48,
          buttonTheme: { applePay: 'black', googlePay: 'black' },
          layout: { maxColumns: 1, maxRows: 2 },
        }}
      />

      {walletReady && (
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/40 text-xs uppercase tracking-wider">o paga con tarjeta</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>
      )}

      <PaymentElement options={{
        layout: 'tabs',
        wallets: { applePay: 'never', googlePay: 'never', link: 'never' },
      }} />

      {error && (
        <p className="text-[#F82C47] text-sm text-center" data-testid="embedded-payment-error">{error}</p>
      )}

      <button
        type="submit"
        disabled={!stripe || submitting}
        className="w-full py-4 rounded-full overflow-hidden text-white font-bold text-lg disabled:opacity-60"
        style={{ backgroundColor: '#F82C47' }}
        data-testid="embedded-pay-button"
      >
        {submitting ? (
          <span key="loading" className="flex items-center justify-center gap-2">
            <Loader2 size={20} className="animate-spin" /> Procesando...
          </span>
        ) : (
          <span key="ready" className="flex items-center justify-center gap-2">
            <Lock size={18} /> Pagar {amount.toFixed(2)}€ de forma segura
          </span>
        )}
      </button>
      <div className="flex items-center justify-center gap-2 text-white/50 text-xs">
        <ShieldCheck size={12} /> Procesado por Stripe · Encriptado SSL
      </div>
    </form>
  );
};

const EmbeddedPaymentStep = ({ clientSecret, amount, eventName, onSuccess, onError }) => {
  const options = useMemo(() => ({ clientSecret, appearance: APPEARANCE }), [clientSecret]);

  if (!STRIPE_PK) {
    return (
      <div className="px-5 py-6 text-center text-[#F82C47] text-sm">
        Stripe no está configurado (falta REACT_APP_STRIPE_PK).
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
        <Loader2 size={20} className="animate-spin" /> Preparando pago seguro…
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={options}>
      <InnerForm amount={amount} eventName={eventName} onSuccess={onSuccess} onError={onError} />
    </Elements>
  );
};

export default EmbeddedPaymentStep;
